import { Link } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import CategoryCards from "@/components/CategoryCards";
import SectionHeader from "@/components/SectionHeader";
import { ProductCard } from "@/components/ProductCard";
import { useCategories, useProducts } from "@/hooks/useCatalog";
import { useStore } from "@/context/StoreContext";
import { useSeo } from "@/lib/seo";

export function CategoriesPage() {
  const { data: categories = [] } = useCategories();
  useSeo({
    title: "Nos catégories — ISMO-TECH",
    description: "Windows, Microsoft Office, antivirus, Adobe, outils, déblocage & services, jeux et abonnements.",
  });

  return (
    <div className="container-it py-6 lg:py-12">
      <h1 className="font-display text-2xl font-extrabold text-ink sm:text-4xl" data-testid="categories-title">
        Nos catégories
      </h1>
      <p className="mt-1.5 text-sm text-slate-500">Trouvez rapidement le type de licence ou de service qu'il vous faut.</p>

      <div className="mt-8 lg:hidden">
        <CategoryCards categories={categories} variant="mobile" />
      </div>
      <div className="mt-8 hidden lg:block">
        <CategoryCards categories={categories} />
      </div>

      <Link
        to="/boutique"
        className="mt-8 flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand"
      >
        Voir tout le catalogue <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export function PromotionsPage() {
  const { data, isLoading } = useProducts({ promo: true, sort: "discount", limit: 24 });
  useSeo({
    title: "Promotions — ISMO-TECH",
    description: "Profitez des meilleures offres du moment sur les licences Windows, Office, antivirus et Adobe.",
  });

  return (
    <div className="container-it py-6 lg:py-12">
      <div className="rounded-2xl bg-night p-6 sm:p-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-rose-400">Offres limitées</p>
        <h1 className="mt-2 font-display text-2xl font-extrabold text-white sm:text-4xl" data-testid="promotions-title">
          Les meilleures offres du moment
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-300 sm:text-base">
          Packs et licences sélectionnés à prix réduits. Les réductions sont appliquées automatiquement au panier.
        </p>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
        {!isLoading && (data?.items || []).map((product, i) => (
          <ProductCard key={product.id} product={product} index={i} />
        ))}
      </div>
    </div>
  );
}

export function FavoritesPage() {
  const { favorites, currency } = useStore();
  const ids = favorites.map((f) => f.product_id);
  const { data } = useProducts({ limit: 60 });
  const items = (data?.items || []).filter((p) => ids.includes(p.id));

  useSeo({ title: "Mes favoris — ISMO-TECH", noIndex: true });

  return (
    <div className="container-it py-6 lg:py-12">
      <SectionHeader title="Mes favoris" subtitle="Vos coups de cœur" testId="favorites-title" />
      {items.length ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4" data-testid="favorites-grid">
          {items.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-line py-16 text-center" data-testid="favorites-empty">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
            <Heart className="h-7 w-7" />
          </span>
          <p className="mt-5 font-display text-lg font-bold text-ink">Aucun favori pour l'instant</p>
          <p className="mt-2 text-sm text-slate-500">
            Touchez le cœur sur un produit pour le retrouver ici, {currency === "XOF" ? "sans engagement" : ""}.
          </p>
          <Link
            to="/boutique"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white"
          >
            Parcourir la boutique <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
